import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";

import { useLingui } from "@lingui/react/macro";
import { isLegalSegment, type LegalSegment } from "@pontimed/ui-legal";
import { useNavigate, useRouterState } from "@tanstack/react-router";

import { LegalShell } from "@/components/legal/legal-shell";
import { LocaleSwitcher } from "@/components/layout/locale-switcher";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";

interface LegalSheetContextValue {
  /** Open the legal sheet on the given segment (defaults to "terms"). */
  open: (segment?: LegalSegment) => void;
  /** Close the legal sheet. */
  close: () => void;
}

const LegalSheetContext = createContext<LegalSheetContextValue | null>(null);

/**
 * Mounts a single `<ResponsiveSheet>` at the app root and exposes
 * `useLegalSheet()` to open it from anywhere. URL-driven: the `?legal=<seg>`
 * search param is the source of truth — it survives reload, share-link, push
 * notifications, and Capacitor cold start. Tab changes inside the sheet
 * replace the URL so the link stays shareable to the exact tab. Browser back
 * pops `?legal` and the sheet closes naturally (no history sentinel).
 */
export function LegalSheetProvider({ children }: { children: ReactNode }) {
  const { t } = useLingui();
  const navigate = useNavigate();

  // Read `?legal=...` raw from the router so unknown search keys survive any
  // per-route `validateSearch`. Same trick as auth-modal-context.
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const legalParam = useMemo(() => {
    if (typeof searchStr !== "string" || !searchStr) return null;
    const raw = searchStr.startsWith("?") ? searchStr.slice(1) : searchStr;
    const params = new URLSearchParams(raw);
    const value = params.get("legal");
    return value && isLegalSegment(value) ? value : null;
  }, [searchStr]);

  const sheetOpen = legalParam !== null;
  const activeSegment: LegalSegment = legalParam ?? "terms";

  // The router's search schema is the union of every per-route shape, so
  // `legal` is not in the global type — cast through `never` (same trick used
  // by `_anon/find.tsx` for free-form search updates).
  const open = useCallback(
    (segment: LegalSegment = "terms") => {
      void navigate({
        to: ".",
        search: ((prev: Record<string, unknown>) => ({ ...prev, legal: segment })) as never,
      });
    },
    [navigate],
  );

  const close = useCallback(() => {
    void navigate({
      to: ".",
      search: ((prev: Record<string, unknown>) => {
        const next = { ...prev };
        delete next.legal;
        return next;
      }) as never,
      replace: true,
    });
  }, [navigate]);

  // In-sheet tab change — replace so URL stays shareable but doesn't bloat
  // history with one entry per tap.
  const handleSegmentChange = useCallback(
    (segment: LegalSegment) => {
      void navigate({
        to: ".",
        search: ((prev: Record<string, unknown>) => ({ ...prev, legal: segment })) as never,
        replace: true,
      });
    },
    [navigate],
  );

  const value = useMemo<LegalSheetContextValue>(() => ({ open, close }), [open, close]);

  return (
    <LegalSheetContext.Provider value={value}>
      {children}
      <ResponsiveSheet
        open={sheetOpen}
        onClose={close}
        title={t`Legal & policies`}
        headerActions={<LocaleSwitcher />}
        width="2xl"
        disableHistory
        bodyClassName="px-4 sm:px-6"
      >
        <LegalShell activeSegment={activeSegment} onSegmentChange={handleSegmentChange} />
      </ResponsiveSheet>
    </LegalSheetContext.Provider>
  );
}

/**
 * Trigger the legal sheet from anywhere inside `<LegalSheetProvider>`.
 * Returns no-op fallbacks if called outside the provider so a stale import
 * never crashes the build.
 */
export function useLegalSheet(): LegalSheetContextValue {
  const ctx = useContext(LegalSheetContext);
  if (!ctx) return { open: () => {}, close: () => {} };
  return ctx;
}
